// Valeurs partagées par les sections du moteur "couture_v1" — reprises des
// « Design Tokens » du handoff « Couture et Retouches » (AEF Retouches).
// Tout ce qui est COULEUR passe par la palette de la boutique (Studio de
// thème > Palette), jamais par un hex codé en dur : le handoff est le
// contenu par défaut de ce moteur, pas une maquette figée.
//
// Correspondance palette ↔ tokens du handoff (valeurs de départ dans
// admin/OnlineStore/themes/couture_retouches_v1.js) :
//   cream    → fond principal #FAF7F1      ink      → encre #16130F
//   sand     → fond secondaire #F4F0E7     graphite → corps de texte #4B4239
//   stone    → filets (peu utilisé : les filets du handoff sont ink/12 %)
//   ash      → brun clair, labels #6B5E4B  copper   → brun clair 2 #8A7C68
//   gold     → or sourd, numéros #9C8E79   blush    → voile bouton survolé #383024
//
// Fond des bandes sombres (#14110D / #0F0C09) : `ink` (#16130F) assombri
// d'un voile, dérivé plutôt que codé en dur pour suivre la palette. Textes
// sur fond sombre (#E6DDCD / #D8CDBB / #C9BFAE) : `cream` à 85 / 78 / 70 %.
//
// Formes : border-radius 0 partout, aucune ombre, séparation = filets 1px.
// Le Studio > Forme n'a donc aucune prise ici (rounded-btn/card volontairement
// absents), comme le handoff l'exige (« haute fidélité »).

// Conteneur du handoff : max-width 1280px, centré. Le prototype bornait
// TOUT le site à 1280px (fond crème autour) ; ici les FONDS des sections
// (bandes sombres, crème foncé, filets) vont d'un bord à l'autre de la
// fenêtre et seul le CONTENU est centré sur 1280px — sur un grand écran,
// un pied de page sombre flottant au milieu d'un fond crème ne tenait pas.
export const CONTAINER = "w-full max-w-[1280px] mx-auto";

// Padding de section : 64px 44px (44px → 20px sous md, où 44px de chaque
// côté mangerait un tiers d'un écran de 360px).
export const PAD_X = "px-5 md:px-11";
export const SECTION = `${PAD_X} py-12 md:py-16`;

export const DARK_BG = {
  backgroundColor: "rgb(var(--c-ink, 22 19 15))",
  backgroundImage: "linear-gradient(rgba(0,0,0,0.12), rgba(0,0,0,0.12))",
};

// Voiles photo du handoff.
export const OVERLAY_HERO = "linear-gradient(90deg, rgba(10,8,6,0.88) 0%, rgba(10,8,6,0.55) 55%, rgba(10,8,6,0.25) 100%)";
export const OVERLAY_SIDE = "linear-gradient(90deg, rgba(10,8,6,0.85), rgba(10,8,6,0.3))";
export const OVERLAY_VERTICAL = "linear-gradient(0deg, rgba(10,8,6,0.85), rgba(10,8,6,0.4))";
export const OVERLAY_CENTER = "rgba(10,8,6,0.62)";
// Pied de page : suit la couleur de ZONE « Fond du pied de page » du Studio
// (--c-footer_bg, voir ZONE_PALETTE_KEYS côté serveur), à défaut `ink`
// assombri comme les autres bandes sombres — rendu identique à DARK_BG tant
// que la zone est « hérite ». Le voile de 12 % et celui de la photo (70 %)
// prennent eux aussi la couleur de zone quand elle est choisie : un voile
// noir fixe rendait tout fond clair choisi par l'admin invisible.
export const FOOTER_BG = {
  backgroundColor: "rgb(var(--c-footer_bg, var(--c-ink, 22 19 15)))",
  backgroundImage: "linear-gradient(rgb(var(--c-footer_bg, 0 0 0) / 0.12), rgb(var(--c-footer_bg, 0 0 0) / 0.12))",
};
export const OVERLAY_FOOTER = "rgb(var(--c-footer_bg, 10 8 6) / 0.7)";
// En-tête : texte secondaire (sous-logotype) — zone « Texte secondaire de
// l'en-tête » du Studio, à défaut `ash`, sa couleur du handoff.
export const HEADER_MUTED = { color: "rgb(var(--c-header_muted, var(--c-ash, 107 94 75)))" };

// Typographie (classes Tailwind). `font-display` = Cormorant Garamond,
// `font-sans` = Jost, via la paire "cormorant_jost" (voir FONT_PAIRS).
export const EYEBROW = "text-[11px] tracking-[0.34em] uppercase";
export const EYEBROW_LIGHT = `${EYEBROW} text-ash`;
export const EYEBROW_DARK = `${EYEBROW} text-cream/[0.78]`;
export const BODY = "text-[15px] leading-[1.85] font-light text-graphite [text-wrap:pretty]";
export const BODY_DARK = "text-[15px] leading-[1.85] font-light text-cream/[0.85] [text-wrap:pretty]";
export const H1_HERO = "font-display font-light text-[clamp(30px,4.4vw,58px)] leading-[1.1] text-cream";
export const H2 = "font-display font-light text-[clamp(24px,3vw,40px)] leading-[1.2] text-ink";
export const LABEL = "text-[10px] tracking-[0.2em] uppercase text-ash";

// Boutons : Jost 400, 10px, .22em, majuscules, padding 15px 26px, aucun arrondi.
const BTN_BASE = "inline-flex items-center justify-center text-center text-[10px] tracking-[0.22em] uppercase font-normal py-[15px] px-6 transition-colors duration-150 no-underline";
export const BTN_FILL = `${BTN_BASE} bg-ink text-cream hover:bg-blush hover:text-cream`;
export const BTN_OUTLINE = `${BTN_BASE} border border-ink/[0.35] text-ink hover:bg-ink hover:text-cream`;
export const BTN_INVERSE = `${BTN_BASE} bg-cream text-ink hover:bg-cream/[0.82] hover:text-ink`;

// Filets décoratifs.
export const RULE_LIGHT = "block w-16 h-px bg-ink/25";
export const RULE_DARK = "block w-16 h-px bg-cream/50";

// Un lien interne passe par le routeur ; une ancre pure ou un lien externe
// (http, tel:, mailto:) reste un <a>.
export const isRouterUrl = (url = "") =>
  Boolean(url) && url.startsWith("/") && !url.startsWith("//");

// Numéro affiché → href tel: (chiffres et + uniquement).
export const telHref = (phone = "") => `tel:${String(phone).replace(/[^\d+]/g, "")}`;
